import type { Conversation, EvenementConversation, TableauAdmin, DetailDemande, CalendrierAdmin, ApercuAnnulation, InvitationAdmin, OptionSerieAdmin, SerieAdmin } from './types.ts'
import type { ApercuCourriel, DonneesModelesCourriels, EntreeEnvoiCourriels, EtatEnvoiCourriels } from './types.ts'
import type { ConfigurationCourriels, TypeModeleCourriel } from '@funo/grammaire'
import type { EtatEasterEggDemo } from './demo-easter-egg.tsx'

export class ErreurAdmin extends Error {
  constructor(message: string, readonly statut = 0) {
    super(message)
    this.name = 'ErreurAdmin'
  }
}

function url(base: string, route: string): string {
  return `${base.endsWith('/') ? base.slice(0, -1) : base}${route}`
}

function cleIdempotence(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  return `admin-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

async function verifier(reponse: Response): Promise<Response> {
  if (reponse.ok) return reponse
  const donnees = await reponse.json().catch(() => null) as { erreur?: { message?: string }; message?: string } | null
  throw new ErreurAdmin(donnees?.erreur?.message ?? donnees?.message ?? 'Le serveur d’administration ne répond pas.', reponse.status)
}

async function json<T>(reponse: Response): Promise<T> {
  await verifier(reponse)
  return reponse.json() as Promise<T>
}

export class ClientAdmin {
  private readonly envois = new Map<string, string>()
  constructor(private readonly baseUrl = '') {}

  tableau(signal?: AbortSignal): Promise<TableauAdmin> {
    return fetch(url(this.baseUrl, '/api/admin/tableau'), { headers: { Accept: 'application/json' }, signal }).then(json<TableauAdmin>)
  }

  visiterDemo(signal?: AbortSignal): Promise<EtatEasterEggDemo> {
    return fetch(url(this.baseUrl, '/api/admin/demo/easter-egg/visite'), {
      method: 'POST', headers: { Accept: 'application/json' }, signal,
    }).then(json<EtatEasterEggDemo>)
  }

  desactiverEasterEggDemo(): Promise<EtatEasterEggDemo> {
    return fetch(url(this.baseUrl, '/api/admin/demo/easter-egg/desactiver'), {
      method: 'POST', headers: { Accept: 'application/json' },
    }).then(json<EtatEasterEggDemo>)
  }

  modelesCourriels(signal?: AbortSignal): Promise<DonneesModelesCourriels> {
    return fetch(url(this.baseUrl, '/api/admin/courriels/modeles'), {
      headers: { Accept: 'application/json' }, signal,
    }).then(json<DonneesModelesCourriels>)
  }

  enregistrerModelesCourriels(configuration: ConfigurationCourriels, revision: number): Promise<DonneesModelesCourriels> {
    return fetch(url(this.baseUrl, '/api/admin/courriels/modeles'), {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ configuration, revision }),
    }).then(json<DonneesModelesCourriels>)
  }

  apercuCourriel(configuration: ConfigurationCourriels, modele: TypeModeleCourriel, signal?: AbortSignal): Promise<ApercuCourriel> {
    return fetch(url(this.baseUrl, '/api/admin/courriels/apercu'), {
      method: 'POST', signal,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ configuration, modele }),
    }).then(json<ApercuCourriel>)
  }

  envoiCourriels(signal?: AbortSignal): Promise<EtatEnvoiCourriels> {
    return fetch(url(this.baseUrl, '/api/admin/courriels/envoi'), { headers: { Accept: 'application/json' }, signal }).then(json<EtatEnvoiCourriels>)
  }

  enregistrerEnvoiCourriels(entree: EntreeEnvoiCourriels): Promise<EtatEnvoiCourriels> {
    return fetch(url(this.baseUrl, '/api/admin/courriels/envoi'), {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(entree),
    }).then(json<EtatEnvoiCourriels>)
  }

  retirerEnvoiCourriels(): Promise<EtatEnvoiCourriels> {
    return fetch(url(this.baseUrl, '/api/admin/courriels/envoi'), { method: 'DELETE', headers: { Accept: 'application/json' } }).then(json<EtatEnvoiCourriels>)
  }

  essaiEnvoiCourriels(): Promise<EtatEnvoiCourriels & { message: string }> {
    return fetch(url(this.baseUrl, '/api/admin/courriels/envoi/essai'), { method: 'POST', headers: { Accept: 'application/json' } }).then(json<EtatEnvoiCourriels & { message: string }>)
  }

  choisirOrganisation(organisation_id: string): Promise<unknown> {
    return this.mutation('/api/admin/organisations/choisir', { organisation_id })
  }

  demanderLien(courriel: string): Promise<unknown> {
    return this.mutation('/admin/lien', { courriel })
  }

  connecter(courriel: string, motDePasse: string): Promise<unknown> {
    return this.mutation('/admin/connexion', {
      courriel,
      mot_de_passe: motDePasse,
    })
  }

  conversation(id: string, signal?: AbortSignal): Promise<Conversation> {
    return fetch(url(this.baseUrl, `/api/admin/conversations/${encodeURIComponent(id)}`), { headers: { Accept: 'application/json' }, signal }).then(json<Conversation>)
  }

  conversations(signal?: AbortSignal): Promise<{ conversations: Array<{ id: string; titre: string; maj_le_utc: string }> }> {
    return fetch(url(this.baseUrl, '/api/admin/conversations'), { signal }).then(json<{ conversations: Array<{ id: string; titre: string; maj_le_utc: string }> }>)
  }

  async envoyerMessage(id: string, texte: string, recevoir: (evenement: EvenementConversation) => void): Promise<Conversation | null> {
    const envoi = `${id}\0${texte}`
    const cle = this.envois.get(envoi) ?? cleIdempotence()
    this.envois.set(envoi, cle)
    const reponse = await verifier(await fetch(url(this.baseUrl, `/api/admin/conversations/${encodeURIComponent(id)}/messages`), {
      method: 'POST',
      headers: { Accept: 'text/event-stream, application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ texte, cle_idempotence: cle }),
    }))
    if ((reponse.headers.get('content-type') ?? '').includes('text/event-stream') && reponse.body) {
      let termine = false
      await lireFluxSse(reponse.body, (evenement) => { if (evenement.type === 'fin') termine = true; recevoir(evenement) })
      if (!termine) throw new ErreurAdmin('La connexion a été interrompue. Réessayez le même message pour retrouver sa réponse sans répéter ses actions.')
      this.envois.delete(envoi)
      return null
    }
    const conversation = await reponse.json() as Conversation
    recevoir({ type: 'fin', conversation })
    this.envois.delete(envoi)
    return conversation
  }

  apercu(service: string): Promise<unknown> {
    return this.mutation(`/api/admin/services/${encodeURIComponent(service)}/apercu`, {})
  }

  publier(service: string, citation: string, confirmation_demonstration = false): Promise<unknown> {
    return this.mutation(`/api/admin/services/${encodeURIComponent(service)}/publier`, { citation, confirmation_demonstration })
  }

  detail(id: string, signal?: AbortSignal): Promise<DetailDemande> {
    return fetch(url(this.baseUrl, `/api/admin/demandes/${encodeURIComponent(id)}`), { signal }).then(json<DetailDemande>)
  }

  calendrier(du: string, au: string, service = '', signal?: AbortSignal): Promise<CalendrierAdmin> {
    return fetch(url(this.baseUrl, `/api/admin/calendrier?${new URLSearchParams({ du, au, service })}`), { signal }).then(json<CalendrierAdmin>)
  }

  horaireCsvUrl(du: string, au: string, service = ''): string {
    return url(this.baseUrl, `/api/admin/calendrier/horaire.csv?${new URLSearchParams({ du, au, service })}`)
  }

  optionsSeries(signal?: AbortSignal): Promise<{ services: OptionSerieAdmin[] }> {
    return fetch(url(this.baseUrl, '/api/admin/demandes/series/options'), { signal }).then(json<{ services: OptionSerieAdmin[] }>)
  }

  listerSeries(signal?: AbortSignal): Promise<{ series: SerieAdmin[] }> {
    return fetch(url(this.baseUrl, '/api/admin/demandes/series'), { signal }).then(json<{ series: SerieAdmin[] }>)
  }

  async creerSerie(entree: {
    service_cle: string; ressource_cle: string; date_locale: string; heure_debut: string; heure_fin: string;
    repetitions: number; locataire: { nom: string; courriel: string; organisation?: string; telephone?: string; adresse?: string };
    offres?: Record<string, number>;
  }): Promise<{ serie: SerieAdmin }> {
    const signature = `serie\0${JSON.stringify(entree)}`
    const cle = this.envois.get(signature) ?? cleIdempotence()
    this.envois.set(signature, cle)
    const resultat = await fetch(url(this.baseUrl, '/api/admin/demandes/series'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...entree, cle_idempotence: cle }),
    }).then(json<{ serie: SerieAdmin }>)
    this.envois.delete(signature)
    return resultat
  }

  contratSerieUrl(id: string): string {
    return url(this.baseUrl, `/api/admin/demandes/series/${encodeURIComponent(id)}/contrat.pdf`)
  }

  envoyerContratSerieDemo(id: string): Promise<{ courrier_id: string; destinataire: string; simulation: boolean }> {
    return this.mutation(`/api/admin/demandes/series/${encodeURIComponent(id)}/envoyer-contrat-demo`, {}) as Promise<{ courrier_id: string; destinataire: string; simulation: boolean }>
  }

  invitations(service: string, signal?: AbortSignal): Promise<{ invitations: InvitationAdmin[] }> {
    return fetch(url(this.baseUrl, `/api/admin/services/${encodeURIComponent(service)}/invitations`), { signal }).then(json<{ invitations: InvitationAdmin[] }>)
  }

  creerInvitation(service: string, organisme: string, jours: number, maximum_demandes: number): Promise<{ invitation: InvitationAdmin & { url: string } }> {
    const expire_le_utc = new Date(Date.now() + jours * 86_400_000).toISOString()
    return this.mutation(`/api/admin/services/${encodeURIComponent(service)}/invitations`, { organisme, expire_le_utc, maximum_demandes }) as Promise<{ invitation: InvitationAdmin & { url: string } }>
  }

  async revoquerInvitation(service: string, id: string): Promise<void> {
    await verifier(await fetch(url(this.baseUrl, `/api/admin/services/${encodeURIComponent(service)}/invitations/${encodeURIComponent(id)}`), { method: 'DELETE' }))
  }

  apercuAnnulation(id: string): Promise<ApercuAnnulation> {
    return fetch(url(this.baseUrl, `/api/admin/demandes/${encodeURIComponent(id)}/annulation`)).then(json<ApercuAnnulation>)
  }

  annuler(id: string, motif: string): Promise<unknown> { return this.mutation(`/api/admin/demandes/${encodeURIComponent(id)}/annuler`, { motif }) }
  envoyerContrat(id: string): Promise<{ courrier_id: string; destinataire: string; simulation: boolean }> {
    return this.mutation(`/api/admin/demandes/${encodeURIComponent(id)}/envoyer-contrat`, {}) as Promise<{ courrier_id: string; destinataire: string; simulation: boolean }>
  }
  importer(document: unknown): Promise<unknown> { return this.mutation('/api/admin/services/importer', { document }) }
  sortir(): Promise<unknown> { return this.mutation('/api/admin/sortir', {}) }

  urlConfiguration(service: string): string { return url(this.baseUrl, `/api/admin/services/${encodeURIComponent(service)}/configuration`) }

  decision(demande: string, decision: 'approuver' | 'refuser', motif = ''): Promise<unknown> {
    return this.mutation(`/api/admin/demandes/${encodeURIComponent(demande)}/decision`, { decision, motif })
  }

  private async mutation(route: string, corps: Record<string, unknown>): Promise<unknown> {
    const reponse = await fetch(url(this.baseUrl, route), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...corps, cle_idempotence: cleIdempotence() }),
    })
    await verifier(reponse)
    return reponse.json().catch(() => ({}))
  }
}

export async function lireFluxSse(flux: ReadableStream<Uint8Array>, recevoir: (evenement: EvenementConversation) => void): Promise<void> {
  const lecteur = flux.getReader()
  const decodeur = new TextDecoder()
  let tampon = ''
  try {
    while (true) {
      const { done, value } = await lecteur.read()
      tampon += decodeur.decode(value, { stream: !done })
      const morceaux = tampon.split(/\r?\n\r?\n/)
      tampon = morceaux.pop() ?? ''
      for (const morceau of morceaux) traiterBlocSse(morceau, recevoir)
      if (done) break
    }
    if (tampon.trim()) traiterBlocSse(tampon, recevoir)
  } finally {
    lecteur.releaseLock()
  }
}

export function traiterBlocSse(bloc: string, recevoir: (evenement: EvenementConversation) => void): void {
  const donnees = bloc
    .split(/\r?\n/)
    .filter((ligne) => ligne.startsWith('data:'))
    .map((ligne) => ligne.slice(5).trimStart())
    .join('\n')
  if (!donnees || donnees === '[DONE]') {
    if (donnees === '[DONE]') recevoir({ type: 'fin' })
    return
  }
  try {
    recevoir(JSON.parse(donnees) as EvenementConversation)
  } catch {
    recevoir({ type: 'erreur', message: 'Une réponse de la conversation était illisible.' })
  }
}
